import { report } from "@chatfood/bug-reporter";
import { getQueryParamValue } from "@chatfood/core-utils";
import {
  abortNavigation,
  defineNuxtRouteMiddleware,
  navigateTo,
  useError,
} from "nuxt/app";
import { isOutletRepoError } from "@chatfood/dbe-client/errors";
import { match } from "ts-pattern";
import { useDirectBackend, usePinia } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";

const abortWithUnexpectedError = (
  message: string = "We could not load the location details. Please try again in a few minutes.",
) => {
  return abortNavigation({
    message,
    statusCode: 500,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  // FIXME: create the support for outlet slug in ensure-store-is-loaded middleware
  // Skip this middleware if Commerce API is enabled (ensure-store-is-loaded will handle it)
  // const { isEnabled } = useFeatureFlag();
  // const isCommerceApiEnabled = await isEnabled("direct.useCommerceApi");
  // if (isCommerceApiEnabled) return;

  const routeOutlet = getQueryParamValue(to.params, "outletSlug");

  if (!routeOutlet) {
    return abortWithUnexpectedError(
      "EnsureOutletIsLoadedMiddleware requires a route with outletSlug parameter.",
    );
  }

  const pinia = usePinia();
  const outlet = useOutletStore(pinia);

  if (outlet.isLoaded && outlet.slug === routeOutlet) {
    return;
  }

  const business = useBusinessStore(pinia);
  const basket = useBasketStore(pinia);

  const { outletRepo } = useDirectBackend();

  const orderingModePrimary = basket.orderingMode;
  const orderingModeFallback = match(orderingModePrimary)
    .with("delivery", () => "pickup" as const)
    .with("pickup", () => "delivery" as const)
    .with("curbside", () => "pickup" as const)
    .exhaustive();

  try {
    const [resPrimary, resFallback] = await Promise.all([
      outletRepo.getOutletBySlug({
        businessId: business.id,
        outletSlug: routeOutlet,
        salesChannel: basket.salesChannel,
        orderingMode: orderingModePrimary,
      }),
      outletRepo.getOutletBySlug({
        businessId: business.id,
        outletSlug: routeOutlet,
        salesChannel: basket.salesChannel,
        orderingMode: orderingModeFallback,
      }),
    ]);

    if (!resPrimary) return;

    outlet.reset();

    const responseByMode: Partial<
      Record<IDomainOrderingMode, typeof resPrimary>
    > = {
      [orderingModePrimary]: resPrimary,
      [orderingModeFallback]: resFallback,
    };

    outlet.patch({
      isLoaded: true,
      id: resPrimary.id,
      name: resPrimary.name,
      slug: resPrimary.slug,
      phone: resPrimary.phone,
      isTaxIncluded: resPrimary.tax.isIncluded,
      timezone: resPrimary.timezone,
      hasDelivery: responseByMode.delivery?.settings.isEnabled ?? false,
      hasPickup: responseByMode.pickup?.settings.isEnabled ?? false,
      hasCurbside: responseByMode.curbside?.settings.isEnabled ?? false,
      address: {
        location: resPrimary.address.location,
        city: resPrimary.address.city,
        lat: resPrimary.address.coordinate.lat,
        lng: resPrimary.address.coordinate.lng,
      },
    });

    if (resPrimary.slug !== routeOutlet) {
      return navigateTo(
        {
          name: to.name,
          params: {
            ...to.params,
            outletSlug: resPrimary.slug,
          },
          query: to.query,
          hash: to.hash,
        },
        {
          replace: true,
          redirectCode: 301,
        },
      );
    }
  } catch (error) {
    if (isOutletRepoError(error) && error.isNotFound()) {
      return abortNavigation({
        message: "Outlet not found",
        statusCode: 404,
      });
    }

    report(error, {
      tags: {
        "code.flag": "middleware-outlet",
      },
    });

    return abortWithUnexpectedError();
  }
});
